import React from "react";
import { useEnum } from "@/api/services/global/enum";
import { IconActionStar } from "@/assets/icons";
import { InnerBox } from "@/components/elements/InnerBox";
import { YEAR_MONTH_DAY_STR_FORMAT } from "@/constants/datetime";
import { EMPTY_STR } from "@/constants/string";
import { TalentInfoItem } from "@/features/talent/components/talent-detail/TalentInfoItem";
import { CollapsibleSectionHeader, useCollapsible } from "@/features/talent/components/talent-common/CollapsibleSectionHeader";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { divider } from "@/theme/colors";
import { FCC } from "@/types/common";
import { TALENT_LOCATION_OPTIONS } from "@/types/enum";
import { convertNumberToZipCode } from "@/utils/input";
import { findLabelByValue } from "@/utils/object";
import { Stack, Typography, Grid, Collapse } from "@mui/material";
import dayjs from "dayjs";

const CIRCLED_NUMBERS = ["①", "②", "③", "④"];

type TTalentBasicInfoProps = {
  defaultValues?: Partial<TTalentForm>;
  /** Hide sensitive PII fields (phone, email, contacts, detailed address) for shared/public view */
  isSharedView?: boolean;
};

export const TalentBasicInfo: FCC<TTalentBasicInfoProps> = ({ defaultValues, isSharedView = false }) => {
  const { data: genderOptions } = useEnum("EGender");
  const { data: contactTypeOptions } = useEnum("ETalentContactType");
  const { expanded, toggle } = useCollapsible();

  return (
    <InnerBox mt={4} p={3} showDivider={false} borderColor={divider.high} bordered={true}>
      <CollapsibleSectionHeader title="基本情報" expanded={expanded} onToggle={toggle}>
        {defaultValues?.isRecommendedTalent && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconActionStar />
            <Typography>おすすめ人材</Typography>
          </Stack>
        )}
      </CollapsibleSectionHeader>

      <Collapse in={expanded}>
      <Stack mt={3}>
        <Grid container spacing={3} alignItems="stretch" sx={{ "& > .MuiGrid-item > *:first-child": { height: "100%" }, "& > .MuiGrid-item > *:first-child > .MuiDivider-root:last-child": { mt: "auto" } }}>
          <Grid item xs={6}>
            <TalentInfoItem label="氏名" value={defaultValues?.fullName ?? EMPTY_STR.TEXT} />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem label="カナ表記" value={defaultValues?.fullNameKana ?? EMPTY_STR.TEXT} />
          </Grid>

          <Grid item xs={6}>
            <TalentInfoItem label="呼び名" value={defaultValues?.nickName ?? EMPTY_STR.TEXT} />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem
              label="生年月日"
              value={
                defaultValues?.birthday && dayjs(defaultValues.birthday).isValid()
                  ? `${dayjs(defaultValues.birthday).format(YEAR_MONTH_DAY_STR_FORMAT)} (${dayjs().diff(dayjs(defaultValues.birthday), "year")}歳)`
                  : ""
              }
            />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem label="人材管理番号" value={defaultValues?.talentNumber ?? EMPTY_STR.TEXT} />
          </Grid>

          <Grid item xs={6}>
            <TalentInfoItem label="国籍" value={defaultValues?.nationality?.nationalityName ?? ""} />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem label="性別" value={findLabelByValue(genderOptions, defaultValues?.gender)} emptyText="" />
          </Grid>

          {/* Hide phone, email, and contact methods in shared view */}
          {!isSharedView && (
            <>
              <Grid item xs={6}>
                <TalentInfoItem label="電話番号" value={defaultValues?.phoneNumber ?? null} />
              </Grid>
              <Grid item xs={6}>
                <TalentInfoItem label="メール" value={defaultValues?.email ?? EMPTY_STR.TEXT} />
              </Grid>

              {defaultValues?.contactMethods && defaultValues.contactMethods.length > 0 ? (
                defaultValues.contactMethods.map((cm, index) => (
                  <React.Fragment key={cm.id ?? index}>
                    <Grid item xs={6}>
                      <TalentInfoItem
                        label={`その他の連絡手段・SNS${CIRCLED_NUMBERS[index] ?? index + 1}`}
                        value={findLabelByValue(contactTypeOptions, cm.contactType)}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <TalentInfoItem
                        label={`その他の連絡先ID等${CIRCLED_NUMBERS[index] ?? index + 1}`}
                        value={cm.contactInfo ?? EMPTY_STR.TEXT}
                      />
                    </Grid>
                  </React.Fragment>
                ))
              ) : (
                <>
                  <Grid item xs={6}>
                    <TalentInfoItem label="その他の連絡手段・SNS" value={EMPTY_STR.TEXT} />
                  </Grid>
                  <Grid item xs={6}>
                    <TalentInfoItem label="その他の連絡先ID等" value={EMPTY_STR.TEXT} />
                  </Grid>
                </>
              )}
            </>
          )}

          <Grid item xs={6}>
            <TalentInfoItem label="居住地" value={findLabelByValue(TALENT_LOCATION_OPTIONS, defaultValues?.location)} />
          </Grid>
          <Grid item xs={6}>
            <TalentInfoItem label="宗教" value={defaultValues?.religion} />
          </Grid>

          <Grid item xs={12}>
            <TalentInfoItem
              label="現住所"
              value={
                isSharedView
                  ? (defaultValues?.prefecture || defaultValues?.city
                      ? `${defaultValues.prefecture ?? ""}${defaultValues.city ?? ""}`.trim()
                      : EMPTY_STR.TEXT)
                  : (defaultValues?.zipCode || defaultValues?.prefecture || defaultValues?.city || defaultValues?.address
                      ? `${defaultValues.zipCode ? convertNumberToZipCode(defaultValues.zipCode) : ""} ${defaultValues.prefecture ?? ""}${defaultValues.city ?? ""}${defaultValues.address ?? ""}`.trim()
                      : EMPTY_STR.TEXT)
              }
            />
          </Grid>

          <Grid item xs={12}>
            <TalentInfoItem label="本国における居住地" value={defaultValues?.country ?? EMPTY_STR.TEXT} isShowDivider={false} />
          </Grid>
        </Grid>
      </Stack>
      </Collapse>
    </InnerBox>
  );
};
